import { useEffect, useState } from 'react';
import { fetchBackendVersion } from '@/core/api/version.service';
import pkg from '../../../package.json';

export interface UseVersionResult {
  /** Versão do manager (build-time, vem do CI via VITE_APP_VERSION). */
  managerVersion: string | null;
  /** Versão do backend (runtime, vem do GET /version). */
  backendVersion: string | null;
  /** "Manager prod-1.1.0" — pronto pra renderizar como 1 linha. */
  managerDisplay: string | null;
  /** "Back prod-1.4.0" — pronto pra renderizar como 1 linha. */
  backendDisplay: string | null;
  /** Legacy: "Manager prod-1.1.0 · Back prod-1.4.0" (mantido pra retrocompat). */
  displayVersion: string | null;
  isLoading: boolean;
}

/**
 * Precedência:
 *  1. `VITE_APP_VERSION` (CI injeta do git tag em prod)
 *  2. `VITE_SENTRY_RELEASE` (mesmo CI, var alternativa)
 *  3. `package.json` version (fallback pra dev — sempre tem valor)
 */
const MANAGER_RAW_VERSION =
  import.meta.env.VITE_APP_VERSION ??
  import.meta.env.VITE_SENTRY_RELEASE ??
  pkg.version ??
  null;

/**
 * Env slug pra prefixar a versão (espelha o /version do backend):
 *   production → 'prod'
 *   staging → 'staging'
 *   resto → 'dev'
 */
function getEnvSlug(): string {
  const env = (import.meta.env.VITE_APP_ENV ?? import.meta.env.MODE ?? 'development').toLowerCase();
  if (env === 'production' || env === 'prod') return 'prod';
  if (env === 'staging' || env === 'stage') return 'staging';
  return 'dev';
}

const MANAGER_VERSION = MANAGER_RAW_VERSION
  ? `${getEnvSlug()}-${MANAGER_RAW_VERSION}`
  : null;

/**
 * Retorna a versão do manager (build-time) e do backend (runtime).
 *
 * - Manager: injetada pelo CI como `VITE_APP_VERSION` a partir do git tag.
 *   Em dev (sem CI), fallback pro package.json.
 * - Backend: fetch único em /version com cache em memória.
 */
export function useVersion(): UseVersionResult {
  const [backendVersion, setBackendVersion] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchBackendVersion().then((data) => {
      if (!cancelled && data?.displayVersion != null) {
        setBackendVersion(data.displayVersion);
      }
      if (!cancelled) setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const managerDisplay = MANAGER_VERSION ? `Manager ${MANAGER_VERSION}` : null;
  const backendDisplay = backendVersion ? `Back ${backendVersion}` : null;
  const displayVersion = [managerDisplay, backendDisplay].filter(Boolean).join(' · ') || null;

  return {
    managerVersion: MANAGER_VERSION,
    backendVersion,
    managerDisplay,
    backendDisplay,
    displayVersion,
    isLoading,
  };
}
